import { setBlank } from 'store/slices/blank-slice';
import type { AskConsentPatientParams, Blank, Blanks, Section, UpdateEstimate } from 'store/types/common';

import { privateApi } from '../private-api';

export const blankApi = privateApi.injectEndpoints({
	endpoints: (builder) => ({
		getBlank: builder.query<Blank, number>({
			query: (id) => `patient-cards/${id}`,
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				const user = localStorage.getItem('userToken') || 'anon';
				return `${endpointName}|${user}|${JSON.stringify(queryArgs)}`;
			},
			keepUnusedDataFor: 0,
			providesTags: (result, error, id) => [{ type: 'PatientCards', id }],
		}),
		getBlanks: builder.query<Blanks, any>({
			query: ({ pageNumber, pageSize, sort, sortField, name }) => ({
				url: `patient-cards/search`,
				method: 'GET',
				params: { pageNumber, pageSize, sort, sortField, name },
				credentials: 'include',
			}),
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				const user = localStorage.getItem('userToken') || 'anon';
				return `${endpointName}|${user}|${JSON.stringify(queryArgs)}`;
			},
			keepUnusedDataFor: 0,
			providesTags: (result) => 
				result
					? [
						...result.content.map(({ id }) => ({ type: 'PatientCards' as const, id: id || undefined })),
						{ type: 'PatientCards', id: 'LIST' },
					]
					: [{ type: 'PatientCards', id: 'LIST' }],
		}),
		upsertBlank: builder.mutation<Blank, Partial<Blank>>({
			query(blankData) {
				return {
					url: 'patient-cards/upsert',
					method: 'POST',
					body: {
						...blankData,
						id: blankData.id || null,
					},
				};
			},
			invalidatesTags: [{ type: "PatientCards" as const, id: "LIST" }],
			async onQueryStarted(args, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(setBlank(data));
				} catch (error) {
					console.error('Error while upserting blank:', error);
				}
			},
		}),

		upsertPatientEstimate: builder.mutation<UpdateEstimate, any>({
			query(body) {
				return {
					url: 'patient-estimate/upsert',
					method: 'POST',
					body,
				};
			},
		}),

		deleteBlank: builder.mutation<void, string | number>({
			query(id) {
				return {
					url: `patient-cards/${id}`,
					method: 'DELETE',
				};
			},
			invalidatesTags: (result, error, id) => [
				{ type: 'PatientCards', id },
				{ type: 'PatientCards', id: 'LIST' }
			],
			async onQueryStarted(id, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;
				} catch (error) {
					console.error('Error while deleting blank:', error);
				}
			},
		}),

		askConsentPatient: builder.mutation<void, AskConsentPatientParams>({
			query: ({ email, name, id }) => ({
				url: `patient-cards/ask-consent-patient?email=${encodeURIComponent(email)}&name=${encodeURIComponent(name)}&id=${id}`,
				method: 'POST',
			}),
		}),

		getSections: builder.query<Section[], void>({
			query: () => `/sections`,
		}),

		addSections: builder.mutation<void, Partial<Section>[]>({
			query(args) {
				return {
					url: '/sections/upsert',
					method: 'POST',
					body: args,
				};
			},

			async onQueryStarted(args, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
				} catch (error) {
					console.error('Error while upserting section:', error);
				}
			},
		}),

		deleteSection: builder.mutation<void, number>({
			query(id) {
				return {
					url: `sections/delete/${id}`,
					method: 'DELETE',
				};
			},
			async onQueryStarted(id, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;
					console.log('Раздел удален');
				} catch (error) {
					console.error('Error while deleting blank:', error);
				}
			},
		}),

		getDownloadBlank: builder.query<Blob, number | number[]>({
			query: (ids) => {
				if (Array.isArray(ids)) {
					const queryParams = ids.map((id) => `ids=${encodeURIComponent(id)}`).join('&');
					return {
						url: `report/patient-cards/download/id?${queryParams}`,
						method: 'GET',
						responseHandler: (response) => response.blob(),
					};
				} else {
					return {
						url: `report/patient-cards/download/id?ids=${encodeURIComponent(ids)}`,
						method: 'GET',
						responseHandler: (response) => response.blob(),
					};
				}
			},
			serializeQueryArgs: () => 'downloadFile',
		}),

		getDownloadBlanks: builder.query<Blob, void>({
			query: () => ({
				url: 'report/patient-cards/download',
				method: 'GET',
				responseHandler: (response) => response.blob(),
			}),
			serializeQueryArgs: () => 'downloadFile',
		}),

		getDownloadPatientCard: builder.query<Blob, number>({
			query: (id) => ({
				url: `report/patient-cards/${id}`,
				method: 'GET',
				responseHandler: (response) => response.blob(),
			}),
			serializeQueryArgs: () => 'downloadFile',
		}),

		getYears: builder.query<number[], void>({
			query: () => `/public/years`,
		}),
	}),

	overrideExisting: false,
});

export const {
	useUpsertBlankMutation,
	useGetBlankQuery,
	useGetBlanksQuery,
	useGetSectionsQuery,
	useDeleteBlankMutation,
	useAddSectionsMutation,
	useDeleteSectionMutation,
	useAskConsentPatientMutation,
	useLazyGetDownloadBlankQuery,
	useLazyGetDownloadBlanksQuery,
	useUpsertPatientEstimateMutation,
	useLazyGetDownloadPatientCardQuery,
	useGetYearsQuery,
} = blankApi;
